import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, BadgeCheck, HeartPulse, Banknote, ShieldCheck,
  Calendar, MessageSquare, Smartphone, MapPin,
} from "lucide-react";
import ShiftsLayout from "../../components/shifts/ShiftsLayout";
import HowItWorks from "../../components/shifts/HowItWorks";
import PageMeta from "../../components/seo/PageMeta";
import { HOW_IT_WORKS, SHIFTS_CTAS } from "../../data/marketplaceData";

const PERKS = [
  { Icon: Banknote,      title: "Weekly W-2 direct deposit", blurb: "Friday by 9 AM. Workers' comp + tax remittance handled by TRITAL Care — never 1099." },
  { Icon: ShieldCheck,   title: "Rate-locked at claim",       blurb: "The hourly you see is the hourly you earn. No bait-and-switch. OT pays time-and-a-half on hours 9–12." },
  { Icon: Calendar,      title: "Build the week you want",    blurb: "Filter by specialty, state, shift type and pay. Save searches. Get text alerts when a matched shift opens." },
  { Icon: HeartPulse,    title: "Specialties we support",     blurb: "RN · LPN · CNA · Allied · Therapy · Advanced Practice. Compact-state license = cross-state shift access." },
  { Icon: Smartphone,    title: "Mobile-first claim flow",    blurb: "Claim a shift in 2 taps from your phone. Upload credentials with the camera. Message the unit lead in-app." },
  { Icon: MessageSquare, title: "Real recruiter desk",        blurb: "Text (832) 344-1525 or email support@tritalcare.com — both staffed by the TRITAL Care team, not a bot tree." },
];

export default function ForClinicians() {
  return (
    <ShiftsLayout>
      <PageMeta
        title="For Clinicians — TRITAL ShiftBridge™ by TRITAL Care®"
        description="Pick up PRN shifts as a W-2 employee with weekly direct deposit, rate transparency, zero clinician fees. RN, LPN, CNA, allied, therapy."
        canonical="https://www.tritalcare.com/shiftbridge/clinicians"
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-trital-mesh">
        <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card">
              <HeartPulse className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
              <span className="overline text-trital-navy">For clinicians</span>
            </div>
            <h1 className="mt-6 font-display font-black text-5xl lg:text-[64px] leading-[1.02] tracking-tight text-slate-900 text-balance">
              The pay you posted.
              <span className="block text-trital-navy">The week you wanted.</span>
            </h1>
            <p className="mt-5 text-slate-600 max-w-[60ch] leading-relaxed text-lg">
              TRITAL ShiftBridge™ is built and operated by TRITAL Care® — a healthcare-only workforce partner. We're the W-2 alternative to gig-economy nursing apps. Real rates. Real recruiters. Real pay-day Friday.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to={SHIFTS_CTAS.clinicianSignup}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-trital-red text-white text-[14px] font-semibold btn-lift hover:bg-trital-red-deep shadow-[0_8px_30px_-8px_rgba(220,38,38,0.5)]"
                data-testid="fc-signup-cta"
              >
                Create clinician account <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              </Link>
              <Link
                to={SHIFTS_CTAS.browse}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-slate-300 text-slate-700 text-[14px] font-semibold hover:border-trital-navy hover:text-trital-navy hover:bg-white"
              >
                Browse open shifts <ArrowUpRight className="w-4 h-4 text-trital-red" strokeWidth={1.8} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <HowItWorks
        eyebrow="Four-step clinician flow"
        title="Sign up. Get verified. Pick up shifts."
        steps={HOW_IT_WORKS.clinician}
        cta={{ label: "Sign up as a clinician", to: SHIFTS_CTAS.clinicianSignup }}
        variant="dark"
        testid="fc-how-it-works"
      />

      {/* PERKS */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24" data-testid="fc-perks">
        <div className="max-w-3xl">
          <div className="overline text-trital-red">What you actually get</div>
          <h2 className="mt-3 font-display font-black text-4xl lg:text-[44px] tracking-tight leading-[1.05] text-slate-900 text-balance">
            None of the gig-economy nonsense. All of the upside.
          </h2>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PERKS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.3, delay: i * 0.04 }}
              className="rounded-3xl bg-white border border-slate-200 shadow-card p-6"
              data-testid={`fc-perk-${i}`}
            >
              <div className="w-11 h-11 rounded-xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center"><p.Icon className="w-5 h-5" strokeWidth={1.8} /></div>
              <div className="mt-5 font-display font-bold text-[17px] text-slate-900">{p.title}</div>
              <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">{p.blurb}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PAY EXAMPLE */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="fc-pay-example">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24 grid lg:grid-cols-[1fr_1.1fr] gap-12 items-center">
          <div>
            <div className="overline text-trital-red">Pay, transparently</div>
            <h2 className="mt-3 font-display font-black text-4xl lg:text-[44px] tracking-tight leading-[1.05] text-slate-900 text-balance">
              Sample week — picked up four 12-hour shifts.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed max-w-[58ch]">
              Real rate. Real OT. Real check Friday at 9 AM. The math below is what an ICU traveler in TX would actually take home in week one.
            </p>
            <ul className="mt-6 space-y-3 text-[14px] text-slate-700">
              {[
                "Mon, Wed: $74/hr × 12 = $888 each",
                "Fri, Sat: $74/hr × 12 = $888 (× 1.5 OT on hours 9–12 = $1,036 each)",
                "Weekly gross ≈ $3,848",
                "Weekly direct deposit Fri 9 AM",
              ].map((b) => (
                <li key={b} className="inline-flex items-start gap-2"><BadgeCheck className="w-4 h-4 mt-0.5 text-trital-red shrink-0" strokeWidth={2} />{b}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl bg-white border border-slate-200 p-7 shadow-card">
            <div className="font-display font-black text-2xl text-slate-900">Weekly stub preview</div>
            <div className="mt-5 space-y-3">
              {[
                ["Reg hours (32)", "$2,368.00"],
                ["OT hours (8)",  "$888.00"],
                ["Holiday diff",  "$0.00"],
                ["Mileage",       "$0.00"],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-[14px]">
                  <div className="text-slate-600">{k}</div>
                  <div className="font-semibold text-slate-900 tabular-nums">{v}</div>
                </div>
              ))}
              <div className="h-px bg-slate-200 my-4" />
              <div className="flex items-center justify-between">
                <div className="font-display font-bold text-[15px] text-slate-900">Gross weekly</div>
                <div className="font-display font-black text-trital-navy text-[24px] tabular-nums">$3,256.00</div>
              </div>
              <div className="text-[11px] text-slate-500 mt-1.5">+ $592 estimated OT premium · Net depends on tax bracket and elections.</div>
            </div>
          </div>
        </div>
      </section>

      {/* SPECIALTIES STRIP */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24" data-testid="fc-specialties">
        <div className="max-w-3xl">
          <div className="overline text-trital-red">Disciplines & specialties</div>
          <h2 className="mt-3 font-display font-black text-4xl lg:text-[44px] tracking-tight leading-[1.05] text-slate-900 text-balance">
            We build shifts for the unit you actually work on.
          </h2>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            "ICU","ED","Med-Surg","L&D","NICU","PICU","OR","PACU","Telemetry","Stepdown","Cath Lab","Wound Care",
            "PT","OT","SLP","Respiratory","Sterile Processing","Phlebotomy","Surg Tech","Radiology Tech","CNA","LPN","Medical Assistant","NP / PA",
          ].map((s) => (
            <div key={s} className="rounded-2xl bg-white border border-slate-200 px-4 py-3 text-[13px] font-semibold text-slate-700 inline-flex items-center gap-2 shadow-card">
              <span className="w-2 h-2 rounded-full bg-trital-red" /> {s}
            </div>
          ))}
        </div>
      </section>

      {/* CTA BAND */}
      <section className="bg-trital-navy-ink text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-trital-navy-mesh opacity-90" />
        <div className="absolute inset-0 dot-grid opacity-10" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-24 text-center">
          <MapPin className="w-7 h-7 text-trital-red mx-auto" strokeWidth={1.8} />
          <h2 className="mt-4 font-display font-black text-4xl lg:text-[44px] tracking-tight leading-[1.05] text-white text-balance">
            Your next shift could be 14 minutes from your driveway.
          </h2>
          <p className="mt-4 text-white/75 max-w-[58ch] mx-auto">Create your clinician account in under 2 minutes. Most clinicians are credential-cleared and shift-eligible within 72 hours.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={SHIFTS_CTAS.clinicianSignup} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-trital-red text-white text-[14px] font-semibold btn-lift hover:bg-trital-red-deep" data-testid="fc-bottom-cta">
              Create clinician account <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </Link>
            <Link to={SHIFTS_CTAS.browse} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white text-[14px] font-semibold hover:bg-white/15">
              See open shifts now
            </Link>
          </div>
        </div>
      </section>
    </ShiftsLayout>
  );
}
